import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Hybrid auth strategy:
// - Primary: Bearer token in Authorization header (works everywhere,
//   including mobile in-app webviews where 3rd-party cookies get stripped)
// - Secondary: httpOnly cookie (set by the server on login) — adds defense
//   in depth on desktop browsers where it flows naturally.
// Token is kept in sessionStorage (tab-scoped, safer than localStorage for
// XSS because it doesn't persist across tabs/sessions).
const TOKEN_KEY = "tc_token";

export const getToken = () => sessionStorage.getItem(TOKEN_KEY);
export const setToken = (t) => {
  if (t) sessionStorage.setItem(TOKEN_KEY, t);
  else sessionStorage.removeItem(TOKEN_KEY);
};

export const api = axios.create({ baseURL: API, withCredentials: true });

api.interceptors.request.use((config) => {
  const t = getToken();
  if (t && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${t}`;
  }
  return config;
});

api.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err?.response?.status === 401) {
      // A 401 on a LOGIN endpoint is just "wrong credentials" — let the
      // caller show an inline error. We only treat it as "session expired"
      // for authenticated API calls.
      const urlPath = err?.config?.url || "";
      const isLoginCall =
        urlPath.includes("/auth/login") ||
        urlPath.includes("/auth/pin-login") ||
        urlPath.includes("/auth/logout");
      const path = window.location.pathname;
      const isOnLoginPage = path === "/login" || path.startsWith("/danseuse/login");
      if (!isLoginCall && !isOnLoginPage) {
        setToken(null);
        localStorage.removeItem("tc_user");
        window.location.href = "/login";
      }
    }
    // Normalize FastAPI 422 detail (array of objects) into a readable string
    const d = err?.response?.data?.detail;
    if (Array.isArray(d)) {
      err.response.data.detail = d
        .map((x) => (x && typeof x === "object" ? x.msg || JSON.stringify(x) : String(x)))
        .join(", ");
    } else if (d && typeof d === "object") {
      err.response.data.detail = d.msg || JSON.stringify(d);
    }
    return Promise.reject(err);
  }
);

export const fmtMoney = (n) =>
  new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(n || 0);

export const fmtDate = (s) => {
  if (!s) return "-";
  try {
    return new Date(s).toLocaleDateString("fr-FR");
  } catch {
    return s;
  }
};

export const openPdf = (path) => {
  if (!path) return;
  const t = getToken();
  // Cookie is httpOnly so window.open carries it on same-origin; we also add
  // ?auth= as a fallback for environments where cookies don't flow (mobile
  // webviews). Backend accepts both.
  const url = t ? `${API}/files/${path}?auth=${encodeURIComponent(t)}` : `${API}/files/${path}`;
  window.open(url, "_blank", "noopener");
};
